import * as React from 'react';

import type { DocumentTableDefinitionSelect } from '@gitbook/api-client';
import {
    type BlockTableData,
    type TableColumnDefinition,
    type TableView,
    table,
} from '@gitbook/doc-core';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { confirm, toast } from '@gitbook/spine-react';
import type { DeepReadonly } from '@gitbook/util-immutable';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { capitalizeFirstLetter } from '../../../utils';
import { useContentReferenceContext } from '../../ContentReferences';
import { searchOrganizationMembers } from '../../Search';
import { UserAvatar } from '../../Users';
import { definitionSpecs } from '../table/ColumnDefinitions';
import { createSelectOptionsPalette } from '../table/ColumnDefinitions/select';
import type { TableRecordKV } from '../table/types';
import { type TableDefinitionViewCommandsParams, viewSpecs } from '../table/Views';

/** Main hook params - typed based on column type */
type UseCardFieldOptionsParams = BaseCardFieldOptionsParams & FilesCardFieldOptionsParams;

/** Base interface for all field options */
interface BaseCardFieldOptionsParams {
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Current view in the table */
    view: TableView;
    /** Column definition of the column */
    columnDefinition: TableColumnDefinition;
    /** Optional callback for value changes (needed for insert mode) */
    onValueChange?: (newValue: any) => Promise<void> | void;
    /** The record for the current field */
    record: TableRecordKV;
}

interface FilesCardFieldOptionsParams {
    /** Optional resolved file data for file fields */
    resolvedFiles?: ResolvedFiles;
    /** Optional insertFiles function for file fields */
    insertFiles?: InsertFiles;
}

type ResolvedFiles = Array<{
    fileId: string;
    name: string;
    href?: string;
}>;

type InsertFiles = {
    prompt: (options?: {
        title?: string;
    }) => Promise<Array<{ kind: 'file'; file: string } | { kind: 'url'; url: string }>>;
};

/**
 * Record-level operations for different column types.
 * These are specific to editing a field value, not column settings.
 */
const TableRecordDefinitions: {
    [type: string]: {
        commands: (params: {
            onValueChange?: (newValue: any) => Promise<void> | void;
            nodeKey?: string;
            editor?: EditorContainer;
            record: TableRecordKV;
            columnDef: DeepReadonly<TableColumnDefinition>;
            resolvedFiles?: Array<{
                fileId: string;
                name: string;
                href?: string;
            }>;
            insertFiles?: InsertFiles;
            organization?: string;
        }) => PaletteCommandDef[];
    };
} = {
    // TODO: add content-ref commands (remove/replace/open link)
    users: {
        commands: ({ onValueChange, record, columnDef, organization }) => {
            if (!onValueChange || !organization) return [];

            const currentValue = (record.value.values[columnDef.id] as string[]) || [];

            return [
                {
                    key: 'record.users.select',
                    title: currentValue.length > 0 ? 'Edit users' : 'Add users',
                    icon: currentValue.length > 0 ? Icon.Pencil : Icon.Plus,
                    descendant: () =>
                        createPalette({
                            search: { placeholder: 'Search users…' },
                            groups: [
                                {
                                    key: 'users',
                                    title: 'Users',
                                    commands: (query) => {
                                        const members = searchOrganizationMembers({
                                            organizationId: organization,
                                            query,
                                        });

                                        // Sort results: selected users first, then alphabetically
                                        const sortedResults = members.sort((a, b) => {
                                            const aSelected = currentValue.includes(a.user.id);
                                            const bSelected = currentValue.includes(b.user.id);

                                            if (aSelected && !bSelected) return -1;
                                            if (!aSelected && bSelected) return 1;

                                            return a.user.displayName.localeCompare(
                                                b.user.displayName
                                            );
                                        });

                                        return sortedResults.map((member) => {
                                            const isSelected = currentValue.includes(
                                                member.user.id
                                            );

                                            return {
                                                key: member.user.id,
                                                title: member.user.displayName,
                                                icon: () => (
                                                    <UserAvatar user={member.user} size="xs" />
                                                ),
                                                selected: isSelected,
                                                type: PaletteCommandType.CHECKBOX,
                                                trigger: () => {
                                                    const newValue = isSelected
                                                        ? currentValue.filter(
                                                              (id) => id !== member.user.id
                                                          )
                                                        : [...currentValue, member.user.id];
                                                    onValueChange(newValue);
                                                },
                                            };
                                        });
                                    },
                                },
                            ],
                        }),
                },
            ];
        },
    },
    files: {
        commands: ({ onValueChange, record, columnDef, resolvedFiles, insertFiles }) => {
            assert(onValueChange !== undefined);

            const value = record.value.values[columnDef.id] as string[];

            const addFilesCommand: PaletteCommandDef = {
                key: 'record.files.add',
                title: 'Add files',
                icon: Icon.Plus,
                trigger: async () => {
                    assert(insertFiles !== undefined);

                    const refs = await insertFiles.prompt({
                        title: 'Select files',
                    });

                    const newFileIds = refs
                        .filter((ref) => ref.kind === 'file')
                        .map((ref) => ref.file);

                    if (newFileIds.length > 0) {
                        const newValue = [...(value || []), ...newFileIds];
                        onValueChange(newValue);
                    }
                },
            };

            if (!Array.isArray(value) || value.length === 0) {
                return [addFilesCommand];
            }

            // Create individual commands for each file in this cell
            const fileCommands = value.map((fileId, index) => {
                const onRemove = () => {
                    const newValue = [...value];
                    newValue.splice(index, 1);
                    onValueChange(newValue);
                };

                // Find resolved file data for this fileId
                const resolvedFile = resolvedFiles?.find((f) => f.fileId === fileId);
                const fileName = resolvedFile?.name || `File ${index + 1}`;
                const fileHref = resolvedFile?.href;

                return {
                    key: `record.files.${fileId}`,
                    title: fileName,
                    icon: Icon.Paperclip,
                    descendant: () => {
                        return createPalette({
                            groups: [
                                {
                                    key: 'file',
                                    commands: [
                                        fileHref
                                            ? {
                                                  key: 'download',
                                                  title: 'Download',
                                                  icon: Icon.Download,
                                                  trigger: () => {
                                                      window.open(fileHref, '_blank');
                                                  },
                                              }
                                            : null,
                                        {
                                            key: 'remove',
                                            title: 'Remove',
                                            icon: Icon.Trashcan,
                                            trigger: () => {
                                                onRemove();
                                            },
                                        },
                                    ].filter(filterOutNullable),
                                },
                            ],
                        });
                    },
                };
            });

            return [addFilesCommand, ...fileCommands];
        },
    },
    select: {
        commands: ({ onValueChange, nodeKey, editor, record, columnDef }) => {
            assert(onValueChange !== undefined);

            const selectDef = columnDef as DocumentTableDefinitionSelect;

            const value = record.value.values[selectDef.id] as string[];
            const multipleAllowed = selectDef.multiple;

            return [
                {
                    key: 'record.select.insert',
                    title:
                        value.length > 0
                            ? multipleAllowed
                                ? 'Edit options'
                                : 'Edit option'
                            : multipleAllowed
                              ? 'Add options'
                              : 'Add option',
                    icon: value.length > 0 ? Icon.Pencil : Icon.Plus,
                    descendant: () =>
                        createSelectOptionsPalette({
                            definition: selectDef,
                            value: record.value.values[selectDef.id] as string[],
                            nodeKey,
                            editor,
                            onValueChange,
                        }),
                },
            ];
        },
    },
};

export function useCardFieldOptions(params: UseCardFieldOptionsParams) {
    const { nodeKey, editor, view, columnDefinition, onValueChange, record } = params;

    const contentReferenceContext = useContentReferenceContext();

    // Extract field-specific props based on column type
    const getFieldSpecificProps = () => {
        switch (columnDefinition.type) {
            case 'files':
                return {
                    resolvedFiles: params.resolvedFiles,
                    insertFiles: params.insertFiles,
                };
            case 'users':
                return {
                    organization: contentReferenceContext.organization,
                };
            default:
                return {};
        }
    };

    const fieldSpecificProps = getFieldSpecificProps();

    const refresh = React.useCallback((): [
        PaletteDef,
        TableDefinitionViewCommandsParams<TableView, TableColumnDefinition>,
    ] => {
        const tableNode = editor.value.document.assertBlock(nodeKey);
        const data = tableNode.data.toFrozenObject<BlockTableData>();
        const definition = data.definition[columnDefinition.id];

        return [
            createPaletteCommands(data.view, definition, record, fieldSpecificProps),
            generateCommandsParams(data.view, definition),
        ];
    }, [editor, nodeKey, columnDefinition.id, record, fieldSpecificProps]);

    const generateCommandsParams = React.useCallback(
        (
            currentView: DeepReadonly<TableView>,
            currentDefinition: DeepReadonly<TableColumnDefinition>
        ) => {
            return {
                definition: currentDefinition,
                view: currentView,
                setView: (newView: TableView) => {
                    editor.change((change) => {
                        table.setView(change, nodeKey, newView);
                    });
                    return refresh();
                },
                setDefinition: (newDefinition: TableColumnDefinition) => {
                    editor.change((change) => {
                        table.setColumnDefinition(
                            change,
                            nodeKey,
                            currentDefinition.id,
                            newDefinition
                        );
                    });
                    return refresh();
                },
            } as TableDefinitionViewCommandsParams<TableView, TableColumnDefinition>;
        },
        [editor, nodeKey, refresh]
    );

    const createPaletteCommands = React.useCallback(
        (
            currentView: DeepReadonly<TableView>,
            currentDefinition: DeepReadonly<TableColumnDefinition>,
            currentRecord: TableRecordKV,
            fieldProps: {
                resolvedFiles?: ResolvedFiles;
                insertFiles?: InsertFiles;
            }
        ): PaletteDef => {
            const spec = definitionSpecs[currentDefinition.type];
            const viewSpec = viewSpecs[currentView.type];
            const params = generateCommandsParams(currentView, currentDefinition);

            // Get column-level commands (show/hide/delete column, etc.)
            let columnCommands = spec.commands?.(params) ?? [];

            // For select columns, pass the current record value to disable "Allow multiple" when needed
            if (currentDefinition.type === 'select' && columnCommands.length > 0) {
                const currentValue = record.value.values[currentDefinition.id] as
                    | string[]
                    | undefined;
                const selectParams = { ...params, currentValue, onValueChange } as any;
                columnCommands = spec.commands?.(selectParams) ?? [];
            }

            // Flatten columnCommands if they are groups (like from selectColumnCommands)
            const flattenedColumnCommands: PaletteCommandDef[] = columnCommands.flatMap((group) => {
                if ('commands' in group) {
                    const commands = group.commands;
                    if (Array.isArray(commands)) {
                        return commands;
                    }
                    // If commands is a function, we can't flatten it here
                    return [];
                }
                return [group];
            });

            if (flattenedColumnCommands.length > 0 && !flattenedColumnCommands[0].title) {
                flattenedColumnCommands[0] = {
                    ...flattenedColumnCommands[0],
                    title: 'Global options',
                };
            }

            // Get view-specific definition commands
            const definitionCommands = viewSpec.definitionCommands
                ? viewSpec.definitionCommands(params)
                : [];

            // Flatten definitionCommands as well
            const flattenedDefinitionCommands: PaletteCommandDef[] = definitionCommands.flatMap(
                (group) => {
                    if ('commands' in group) {
                        const commands = group.commands;
                        if (Array.isArray(commands)) {
                            return commands;
                        }
                        return [];
                    }
                    return [group];
                }
            );

            // Get record-level commands (field-specific operations)
            const recordDefinition = TableRecordDefinitions[currentDefinition.type];
            const recordCommands =
                recordDefinition?.commands({
                    columnDef: currentDefinition,
                    onValueChange,
                    nodeKey,
                    editor,
                    record: currentRecord,
                    ...fieldProps,
                }) ?? [];

            // Common danger commands for all column types
            const dangerCommands: PaletteCommandDef[] = [
                {
                    key: 'definition.hide',
                    title: `Hide ${viewSpec.definitionName}`,
                    icon: Icon.EyeOff,
                    trigger: () => {
                        void confirm({
                            title: `Hide ${viewSpec.definitionName}`,
                            description:
                                'Hiding a field hides it from all cards, you can open the "Edit card" dialog to show it again.',
                            kind: 'danger',
                            confirmLabel: 'Confirm',
                            onConfirm: async (_event, { preventClose }) => {
                                await toast.catch(
                                    async () => {
                                        editor.change((change) => {
                                            table.hideColumn(change, nodeKey, currentDefinition.id);
                                        });
                                        toast.success(
                                            `${capitalizeFirstLetter(viewSpec.definitionName)} is hidden from ${currentView.type}`
                                        );
                                    },
                                    `Error hiding ${viewSpec.definitionName}`,
                                    {
                                        onReject: preventClose,
                                    }
                                );
                            },
                        });
                    },
                },
                {
                    key: 'definition.delete',
                    title: `Delete ${viewSpec.definitionName}`,
                    icon: Icon.Trashcan,
                    trigger: () => {
                        void confirm({
                            title: `Delete ${viewSpec.definitionName}`,
                            description:
                                'Deleting a field removes it from all cards, and all data in this field will be lost.',
                            kind: 'danger',
                            confirmLabel: 'Confirm',
                            onConfirm: async (_event, { preventClose }) => {
                                await toast.catch(
                                    () => {
                                        editor.change((change) => {
                                            table.removeColumn(
                                                change,
                                                nodeKey,
                                                currentDefinition.id
                                            );
                                        });
                                        toast.success(
                                            `${capitalizeFirstLetter(viewSpec.definitionName)} deleted`
                                        );
                                    },
                                    `Error deleting ${viewSpec.definitionName}`,
                                    { onReject: preventClose }
                                );
                            },
                        });
                    },
                },
            ];

            // Two top-level groups: 'Field options' and 'Settings'
            const groups = [
                {
                    key: 'edit.field',
                    title: 'Field options',
                    commands: recordCommands,
                },
                {
                    key: 'settings',
                    title: 'Settings',
                    commands: [
                        ...flattenedColumnCommands,
                        ...flattenedDefinitionCommands,
                        ...dangerCommands,
                    ],
                },
            ];

            return createPalette({
                groups,
            });
        },
        [nodeKey, editor, generateCommandsParams, onValueChange, record.value.values]
    );

    return {
        createPaletteCommands: () =>
            createPaletteCommands(view, columnDefinition, record, fieldSpecificProps),
        generateCommandsParams: () => generateCommandsParams(view, columnDefinition),
    };
}
